const initializeNavigation = () => {
  const navigation = document.querySelector("[data-theme-nav]");
  if (!navigation || navigation.dataset.bound === "true") return;

  const mobileToggle = navigation.querySelector("[data-mobile-menu-toggle]");
  const mobileMenu = navigation.querySelector("[data-mobile-menu]");
  const updateNavigation = () => navigation.classList.toggle("is-scrolled", window.scrollY > 24);

  mobileToggle?.addEventListener("click", () => {
    const expanded = mobileToggle.getAttribute("aria-expanded") === "true";
    mobileToggle.setAttribute("aria-expanded", String(!expanded));
    if (mobileMenu) mobileMenu.hidden = expanded;
  });

  window.addEventListener("scroll", updateNavigation, { passive: true });
  updateNavigation();
  navigation.dataset.bound = "true";
};

const initializeShopAllLinks = (root = document) => {
  const links = Array.from(root.querySelectorAll?.("[data-shop-all-link]") || []);

  links.forEach((link) => {
    if (link.dataset.shopAllBound === "true") return;
    link.addEventListener("click", (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) return;

      event.preventDefault();
      window.location.assign(link.href);
    });
    link.dataset.shopAllBound = "true";
  });
};

const initializeHomeMotion = (root = document.querySelector("[data-home-motion]")) => {
  if (!root || root.dataset.homeMotionBound === "true") return;

  const hero = root.querySelector("[data-home-hero]");
  const revealSections = Array.from(root.querySelectorAll("[data-home-reveal]"));
  const formulaCards = Array.from(root.querySelectorAll(".formula-compare-card"));
  const duo = root.querySelector(".daily-duo");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  revealSections.forEach((section) => {
    Array.from(section.querySelectorAll("[data-home-cascade]")).forEach((item, index) => {
      item.style.setProperty("--home-delay", `${Math.min(index, 4) * 0.1}s`);
    });
    Array.from(section.querySelectorAll("[data-home-reveal-group] > *")).forEach((item, index) => {
      item.style.setProperty("--home-delay", `${index * 0.14}s`);
    });
  });

  const showEverything = () => {
    revealSections.forEach((section) => section.classList.add("is-visible"));
    formulaCards.forEach((card, index) => card.classList.toggle("is-home-active", index === 0));
  };

  if (reducedMotion) {
    showEverything();
    root.dataset.homeMotionBound = "true";
    return;
  }

  root.classList.add("home-motion-ready");
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        revealObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.14, rootMargin: "0px 0px -7% 0px" },
  );
  revealSections.forEach((section) => revealObserver.observe(section));

  let frameRequested = false;
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const updateMotion = () => {
    frameRequested = false;
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    root.style.setProperty("--home-page-progress", clamp(window.scrollY / maxScroll).toFixed(4));

    if (hero) {
      const rect = hero.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, rect.height));
      root.style.setProperty("--home-hero-shift", `${progress * 46}px`);
      root.style.setProperty("--home-hero-scale", String(1.035 + progress * 0.045));
      root.style.setProperty("--home-hero-copy-shift", `${progress * -34}px`);
      root.style.setProperty("--home-hero-copy-opacity", String(1 - progress * 0.68));
    }

    revealSections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) section.classList.add("is-visible");
    });

    if (formulaCards.length) {
      const viewportCenter = window.innerHeight * 0.55;
      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      formulaCards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < closestDistance) {
          closest = card;
          closestDistance = distance;
        }
      });
      formulaCards.forEach((card) => card.classList.toggle("is-home-active", card === closest));
    }

    if (duo) {
      const rect = duo.getBoundingClientRect();
      const progress = clamp((window.innerHeight - rect.top) / Math.max(1, window.innerHeight + rect.height));
      root.style.setProperty("--home-duo-shift", `${(progress - 0.5) * 28}px`);
    }
  };

  const requestFrame = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(updateMotion);
  };

  window.addEventListener("scroll", requestFrame, { passive: true });
  window.addEventListener("resize", requestFrame);
  requestFrame();
  root.dataset.homeMotionBound = "true";
};

const initializeMoments = (root = document) => {
  const tabs = Array.from(root.querySelectorAll("[data-moment-tab]"));
  const panel = root.querySelector("[data-home-ritual-panel]");
  if (!tabs.length || !panel || panel.dataset.momentsBound === "true") return;

  const time = panel.querySelector("[data-panel-time]");
  const title = panel.querySelector("[data-panel-title]");
  const text = panel.querySelector("[data-panel-text]");
  const image = panel.querySelector("[data-panel-img]");

  const selectMoment = (selected) => {
    tabs.forEach((tab) => {
      const active = tab === selected;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-pressed", String(active));
    });
    if (time) time.textContent = selected.dataset.momentTime || "";
    if (title) title.textContent = selected.dataset.momentTitle || "";
    if (text) text.textContent = selected.dataset.momentText || "";
    if (image && selected.dataset.momentSrc) {
      image.style.opacity = "0";
      window.setTimeout(() => {
        image.src = selected.dataset.momentSrc;
        image.alt = selected.dataset.momentAlt || "";
        image.style.opacity = "1";
      }, 160);
    }
  };

  tabs.forEach((tab) => tab.addEventListener("click", () => selectMoment(tab)));
  panel.dataset.momentsBound = "true";
};

const initializeShopCollection = (root = document) => {
  const shop = root.matches?.("[data-ln-shop]") ? root : root.querySelector?.("[data-ln-shop]");
  if (!shop || shop.dataset.shopBound === "true") return;

  const filters = Array.from(shop.querySelectorAll("[data-shop-filter]"));
  const items = Array.from(shop.querySelectorAll("[data-shop-item]"));
  const grid = shop.querySelector("[data-shop-grid]");
  const sort = shop.querySelector("[data-shop-sort]");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let activeSpin = null;

  filters.forEach((filter) => {
    filter.addEventListener("click", () => {
      const value = filter.dataset.shopFilter;
      filters.forEach((item) => {
        const active = item === filter;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      items.forEach((item) => {
        item.hidden = value !== "all" && item.dataset.shopItem !== value;
      });
    });
  });

  if (sort && grid) {
    const modes = {
      featured: (a, b) => Number(a.dataset.rank) - Number(b.dataset.rank),
      "price-asc": (a, b) => Number(a.dataset.price) - Number(b.dataset.price),
      "price-desc": (a, b) => Number(b.dataset.price) - Number(a.dataset.price),
    };
    sort.addEventListener("change", () => {
      const compare = modes[sort.value] || modes.featured;
      items.sort(compare).forEach((item) => grid.append(item));
    });
  }

  shop.querySelectorAll("[data-shop-spin]").forEach((card) => {
    const video = card.querySelector("[data-shop-spin-video]");
    if (!video) return;

    let isEngaged = false;

    const stop = () => {
      isEngaged = false;
      if (activeSpin?.card === card) activeSpin = null;
      card.classList.remove("is-seed-active", "is-spinning");
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        // The video metadata may not be loaded yet.
      }
    };
    const start = () => {
      if (reducedMotion.matches || isEngaged) return;
      if (activeSpin && activeSpin.card !== card) activeSpin.stop();
      isEngaged = true;
      activeSpin = { card, stop };
      card.classList.add("is-seed-active");
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      try {
        video.currentTime = 0;
      } catch {
        // The video metadata may not be loaded yet.
      }
      const playback = video.play();
      playback?.catch(stop);
    };

    video.addEventListener("playing", () => card.classList.add("is-spinning"));
    video.addEventListener("error", stop);
    card.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "touch") start();
    });
    card.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "touch") stop();
    });
    card.addEventListener("mouseover", start);
    card.addEventListener("mouseout", (event) => {
      if (!card.contains(event.relatedTarget)) stop();
    });
    card.addEventListener("focusin", start);
    card.addEventListener("focusout", (event) => {
      if (!card.contains(event.relatedTarget)) stop();
    });

    if (window.matchMedia("(hover: none)").matches && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && entry.intersectionRatio >= 0.58) {
            start();
          } else if (!entry?.isIntersecting || entry.intersectionRatio < 0.18) {
            stop();
          }
        },
        { threshold: [0, 0.18, 0.58] },
      );
      observer.observe(card);
    }

    reducedMotion.addEventListener?.("change", () => {
      if (reducedMotion.matches) stop();
    });
  });

  shop.dataset.shopBound = "true";
};

const initializeCart = (root = document) => {
  if (document.documentElement.dataset.cartBound === "true") return;
  const routesRoot = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
  const cartPath = (suffix) => `${routesRoot}cart${suffix}`;
  const drawerSectionId = "cart-drawer";
  let mutationQueue = Promise.resolve();

  const updateBadge = (itemCount) => {
    document.querySelectorAll(".nav-cart-badge").forEach((badge) => {
      badge.textContent = String(itemCount);
      badge.setAttribute("aria-label", `${itemCount} ${itemCount === 1 ? "item" : "items"} in cart`);
    });
  };

  const requestCart = async () => {
    const response = await fetch(cartPath(".js"), { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Unable to refresh the cart.");
    return response.json();
  };

  const announceCart = (cart) => {
    updateBadge(cart.item_count);
    window.dispatchEvent(new CustomEvent("liveennature:cart-updated", { detail: cart }));
  };

  const getDrawer = () => document.querySelector("[data-cart-drawer]");
  const getCopy = (key, fallback) => getDrawer()?.dataset[key] || fallback;
  const setStatus = (message, isError = false) => {
    document.querySelectorAll("[data-cart-status], [data-cart-page-status]").forEach((status) => {
      status.textContent = message;
      status.classList.toggle("is-error", isError);
    });
  };
  const setBusy = (busy) => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.toggleAttribute("aria-busy", busy);
    drawer.querySelectorAll("button, input").forEach((control) => {
      control.disabled = busy;
    });
  };
  const bindDrawerClose = (drawer) => {
    if (!drawer || drawer.dataset.closeBound === "true") return;
    drawer.addEventListener("close", () => document.body.classList.remove("cart-drawer-open"));
    drawer.dataset.closeBound = "true";
  };
  const openDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    bindDrawerClose(drawer);
    if (!drawer.open) drawer.showModal();
    document.body.classList.add("cart-drawer-open");
    window.requestAnimationFrame(() => drawer.classList.add("is-open"));
  };
  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer?.open) return;
    drawer.classList.remove("is-open");
    window.setTimeout(() => {
      if (drawer.open) drawer.close();
    }, 220);
  };
  const parseSection = (markup, selector) => {
    if (!markup) return null;
    return new DOMParser().parseFromString(markup, "text/html").querySelector(selector);
  };
  const replaceDrawer = (markup, shouldOpen = false) => {
    const replacement = parseSection(markup, "[data-cart-drawer]");
    const current = getDrawer();
    if (!replacement || !current) return false;
    current.replaceWith(replacement);
    bindDrawerClose(replacement);
    if (shouldOpen) openDrawer();
    return true;
  };
  const getCartPageSectionId = () => {
    const page = document.querySelector("[data-cart-page]");
    const section = page?.closest(".shopify-section");
    return section?.id?.replace(/^shopify-section-/, "") || "";
  };
  const replaceCartPage = (markup) => {
    const replacement = parseSection(markup, "[data-cart-page]");
    const current = document.querySelector("[data-cart-page]");
    if (replacement && current) current.replaceWith(replacement);
  };
  const renderCartSections = (sections, options = {}) => {
    if (!sections) return;
    replaceDrawer(sections[drawerSectionId], options.openDrawer === true);
    const pageSectionId = getCartPageSectionId();
    if (pageSectionId && sections[pageSectionId]) replaceCartPage(sections[pageSectionId]);
  };
  const sectionRequestData = () => {
    const ids = [drawerSectionId];
    const pageSectionId = getCartPageSectionId();
    if (pageSectionId) ids.push(pageSectionId);
    return ids;
  };
  const readError = async (response) => {
    try {
      const payload = await response.json();
      return payload.description || payload.message || getCopy("cartError", "Unable to update your cart.");
    } catch {
      return getCopy("cartError", "Unable to update your cart.");
    }
  };
  const enqueueMutation = (task) => {
    mutationQueue = mutationQueue.catch(() => undefined).then(task);
    return mutationQueue;
  };
  const changeLine = (key, quantity) => enqueueMutation(async () => {
    setBusy(true);
    setStatus(getCopy("cartUpdating", "Updating…"));
    try {
      const response = await fetch(cartPath("/change.js"), {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, Math.min(999, Number(quantity) || 0)),
          sections: sectionRequestData(),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(await readError(response));
      const cart = await response.json();
      announceCart(cart);
      renderCartSections(cart.sections, { openDrawer: getDrawer()?.open === true });
      setStatus("");
      return cart;
    } catch (error) {
      setStatus(error.message || getCopy("cartError", "Unable to update your cart."), true);
      throw error;
    } finally {
      setBusy(false);
    }
  });

  root.querySelectorAll(".ln-shop-card__quick-form, .ln-shop-card__duo-form, .ln-product-form, .home-duo-form").forEach((form) => {
    if (form.dataset.ajaxCartBound === "true") return;
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (form.dataset.cartSubmitting === "true") return;

      const submitter = event.submitter || form.querySelector("button[type='submit']");
      const originalMarkup = submitter?.innerHTML;
      form.dataset.cartSubmitting = "true";
      if (submitter) {
        submitter.disabled = true;
        submitter.textContent = getCopy("cartAdding", "Adding…");
      }

      try {
        const formData = new FormData(form);
        formData.set("sections", sectionRequestData().join(","));
        formData.set("sections_url", window.location.pathname);
        const response = await fetch(cartPath("/add.js"), {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        if (!response.ok) throw new Error(await readError(response));

        const addedItem = await response.json();
        const cart = await requestCart();
        announceCart(cart);
        renderCartSections(addedItem.sections, { openDrawer: true });
        if (submitter) submitter.textContent = getCopy("cartAdded", "Added ✓");
      } catch (error) {
        if (submitter) submitter.textContent = error.message || getCopy("cartError", "Try again");
      } finally {
        form.dataset.cartSubmitting = "false";
        window.setTimeout(() => {
          if (submitter) {
            submitter.disabled = false;
            submitter.innerHTML = originalMarkup || "Add to cart";
          }
        }, 1400);
      }
    });
    form.dataset.ajaxCartBound = "true";
  });

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-cart-toggle]");
    if (toggle && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest("[data-cart-drawer-close]")) {
      closeDrawer();
      return;
    }

    const drawer = event.target.closest("[data-cart-drawer]");
    if (drawer && event.target === drawer) {
      closeDrawer();
      return;
    }

    const drawerAction = event.target.closest("[data-cart-quantity-action]");
    if (drawerAction) {
      const line = drawerAction.closest("[data-cart-line]");
      const input = line?.querySelector("[data-cart-drawer-quantity]");
      if (line && input) changeLine(line.dataset.cartKey, Number(input.value) + Number(drawerAction.dataset.cartQuantityAction));
      return;
    }

    const drawerRemove = event.target.closest("[data-cart-remove]");
    if (drawerRemove) {
      changeLine(drawerRemove.dataset.cartRemove, 0);
      return;
    }

    const pageAction = event.target.closest("[data-cart-page-quantity-action]");
    if (pageAction) {
      const line = pageAction.closest("[data-cart-page-line]");
      const input = line?.querySelector("[data-cart-page-quantity]");
      if (line && input) changeLine(line.dataset.cartKey, Number(input.value) + Number(pageAction.dataset.cartPageQuantityAction));
      return;
    }

    const pageRemove = event.target.closest("[data-cart-page-remove]");
    if (pageRemove) changeLine(pageRemove.dataset.cartPageRemove, 0);
  });

  document.addEventListener("change", (event) => {
    const drawerInput = event.target.closest("[data-cart-drawer-quantity]");
    if (drawerInput) {
      const line = drawerInput.closest("[data-cart-line]");
      if (line) changeLine(line.dataset.cartKey, drawerInput.value);
      return;
    }

    const pageInput = event.target.closest("[data-cart-page-quantity]");
    if (pageInput) {
      const line = pageInput.closest("[data-cart-page-line]");
      if (line) changeLine(line.dataset.cartKey, pageInput.value);
    }
  });

  bindDrawerClose(getDrawer());
  document.documentElement.dataset.cartBound = "true";
};

const initializeScrollStory = (story) => {
  if (!story || story.dataset.scrollStoryBound === "true") return;

  const video = story.querySelector("[data-story-scroll-video]");
  const canvas = story.querySelector("[data-story-canvas]");
  const context = canvas?.getContext("2d", { alpha: false });
  const chapters = Array.from(story.querySelectorAll("[data-ingredient-chapter]"));
  const progressLabel = story.querySelector("[data-ingredient-progress]");
  const mobileIndex = story.querySelector("[data-story-mobile-index]");
  const mobileTitle = story.querySelector("[data-story-mobile-title]");
  const mobileBody = story.querySelector("[data-story-mobile-body]");
  if (!video || chapters.length === 0) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileFrames = window.matchMedia("(max-width: 720px)");
  const frameCount = Number(story.dataset.storyFrameCount || 1);
  const startFrame = Number(story.dataset.storyStartFrame || 0);
  const chapterStarts = (story.dataset.storyChapterStarts || "")
    .split(",")
    .map((value) => Number(value.trim()))
    .filter(Number.isFinite);
  const fallbackStarts = chapters.map((_, index) => startFrame + index * ((frameCount - startFrame - 1) / chapters.length));
  const starts = chapterStarts.length === chapters.length ? chapterStarts : fallbackStarts;

  let animationFrame = 0;
  let currentProgress = 0;
  let targetProgress = 0;
  let previousFrameTime = 0;
  let desiredTime = 0;
  let seekInFlight = false;
  let activeSource = "";
  let activeObjectUrl = "";
  let sourceRequest = 0;
  let fallbackTimer = 0;

  const clearFallbackTimer = () => {
    if (!fallbackTimer) return;
    window.clearTimeout(fallbackTimer);
    fallbackTimer = 0;
  };

  const clamp = (value) => Math.min(1, Math.max(0, value));
  const readProgress = () => {
    const rect = story.getBoundingClientRect();
    const travel = Math.max(1, story.offsetHeight - window.innerHeight);
    return clamp(-rect.top / travel);
  };
  const updateChapter = (progress) => {
    const currentFrame = startFrame + progress * Math.max(0, frameCount - 1 - startFrame);
    let index = 0;
    starts.forEach((chapterStart, chapterIndex) => {
      if (currentFrame >= chapterStart) index = chapterIndex;
    });
    chapters.forEach((chapter, chapterIndex) => {
      chapter.classList.toggle("is-story-active", chapterIndex === index);
    });
    const activeChapter = chapters[index];
    if (activeChapter) {
      if (mobileIndex) mobileIndex.textContent = activeChapter.querySelector("p:first-child")?.textContent || "";
      if (mobileTitle) mobileTitle.textContent = activeChapter.querySelector("h3")?.textContent || "";
      if (mobileBody) mobileBody.textContent = activeChapter.querySelector("p:last-child")?.textContent || "";
    }
    if (progressLabel) {
      progressLabel.textContent = `${String(index + 1).padStart(2, "0")} / ${String(chapters.length).padStart(2, "0")}`;
    }
  };
  const paintFrame = () => {
    if (!canvas || !context || video.seeking || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return false;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    try {
      context.drawImage(video, 0, 0, width, height);
      clearFallbackTimer();
      story.classList.remove("is-frame-fallback");
      story.classList.add("is-frame-ready", "is-scroll-story-ready");
      return true;
    } catch {
      return false;
    }
  };
  const render = (timestamp) => {
    const elapsed = previousFrameTime ? Math.min(64, timestamp - previousFrameTime) : 16;
    const damping = 1 - Math.exp(-elapsed / 92);
    previousFrameTime = timestamp;

    if (reducedMotion.matches) {
      currentProgress = 0;
    } else {
      currentProgress += (targetProgress - currentProgress) * damping;
      if (Math.abs(targetProgress - currentProgress) < 0.0001) currentProgress = targetProgress;
    }

    const progress = currentProgress;
    story.style.setProperty("--story-progress", progress.toFixed(5));
    story.classList.toggle("has-story-progress", progress > 0.025);
    updateChapter(progress);

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA && Number.isFinite(video.duration)) {
      const duration = Math.max(0, video.duration - 0.04);
      const startTime = Math.min(duration, (startFrame / Math.max(1, frameCount)) * video.duration);
      desiredTime = Math.min(duration, Math.max(startTime, startTime + progress * (duration - startTime)));
      if (!seekInFlight && Math.abs(video.currentTime - desiredTime) > 0.015) {
        seekInFlight = true;
        video.currentTime = desiredTime;
      } else if (!seekInFlight) {
        paintFrame();
      }
    }

    if (!reducedMotion.matches && currentProgress !== targetProgress) {
      animationFrame = window.requestAnimationFrame(render);
    } else {
      animationFrame = 0;
      previousFrameTime = 0;
    }
  };
  const schedule = () => {
    targetProgress = readProgress();
    if (!animationFrame) animationFrame = window.requestAnimationFrame(render);
  };
  const setSource = () => {
    const source = mobileFrames.matches ? video.dataset.storyVideoMobile : video.dataset.storyVideoDesktop;
    if (!source || source === activeSource) return;
    activeSource = source;
    const request = ++sourceRequest;
    clearFallbackTimer();
    story.classList.remove("is-frame-ready", "is-scroll-story-ready");
    story.classList.remove("is-frame-fallback");
    video.pause();
    seekInFlight = false;
    desiredTime = 0;
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    const loadVideoSource = (nextSource) => {
      if (request !== sourceRequest) return;
      video.src = nextSource;
      video.load();
    };

    // Keep the approved poster visible while the video loads. If a slower
    // preview misses the first-frame deadline, the poster replaces the terse
    // loading message and the video can still take over once ready.
    fallbackTimer = window.setTimeout(() => {
      if (request === sourceRequest && !story.classList.contains("is-frame-ready")) {
        story.classList.add("is-frame-fallback");
      }
    }, 2200);

    fetch(source, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load story video: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (request !== sourceRequest) return;
        if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
        activeObjectUrl = URL.createObjectURL(blob);
        loadVideoSource(activeObjectUrl);
      })
      .catch(() => loadVideoSource(source));
  };

  video.addEventListener("loadedmetadata", schedule);
  video.addEventListener("loadeddata", schedule);
  video.addEventListener("canplay", schedule);
  video.addEventListener("seeked", () => {
    seekInFlight = false;
    paintFrame();
    schedule();
  });
  video.addEventListener("error", () => {
    clearFallbackTimer();
    story.classList.remove("is-frame-ready", "is-scroll-story-ready");
    story.classList.add("is-frame-fallback");
  });
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  mobileFrames.addEventListener?.("change", () => {
    setSource();
    schedule();
  });
  reducedMotion.addEventListener?.("change", () => {
    if (reducedMotion.matches) {
      video.pause();
      sourceRequest += 1;
      clearFallbackTimer();
      if (activeObjectUrl) {
        URL.revokeObjectURL(activeObjectUrl);
        activeObjectUrl = "";
      }
      video.removeAttribute("src");
      video.load();
      story.classList.remove("is-frame-ready", "is-scroll-story-ready");
      story.classList.add("is-frame-fallback");
      currentProgress = 0;
      targetProgress = 0;
      updateChapter(0);
    } else {
      story.classList.remove("is-frame-fallback");
      activeSource = "";
      setSource();
    }
    schedule();
  });

  if (reducedMotion.matches) {
    story.classList.add("is-frame-fallback");
    updateChapter(0);
  } else {
    setSource();
  }
  schedule();
  story.dataset.scrollStoryBound = "true";
};

const initializeProductPage = (root = document) => {
  const page = root.matches?.("[data-ln-product]") ? root : root.querySelector?.("[data-ln-product]");
  if (!page || page.dataset.productBound === "true") return;

  // unitPrice is only a fallback for legacy pack options that lack
  // data-pack-price-cents; variant-backed options always carry their own price.
  const unitPrice = Number(page.dataset.unitPrice || 0);
  const currency = page.dataset.currency || "USD";
  const gallery = page.querySelector("[data-product-gallery]");
  const mainImage = page.querySelector("[data-product-gallery-main]");
  const summaryImages = Array.from(page.querySelectorAll("[data-product-summary-image]"));
  const unitLabels = Array.from(page.querySelectorAll("[data-product-unit]"));
  const priceLabels = Array.from(page.querySelectorAll("[data-product-price], [data-product-add-price]"));
  const quantityInput = page.querySelector("[data-product-quantity]");
  const variantIdInput = page.querySelector("[data-product-variant-id]");
  const installmentVariantInputs = Array.from(page.querySelectorAll("[data-product-installment-variant-id]"));
  const arrivalLabels = Array.from(page.querySelectorAll("[data-product-arrival]"));
  const compareAtLabels = Array.from(page.querySelectorAll("[data-product-compare-at]"));
  const savingsLabels = Array.from(page.querySelectorAll("[data-product-savings]"));
  const galleryThumbs = Array.from(page.querySelectorAll("[data-product-gallery-thumb]"));
  const galleryCopies = Array.from(page.querySelectorAll("[data-product-gallery-copy]"));
  const packOptions = Array.from(page.querySelectorAll("[data-pack-option]"));

  const formatMoney = (cents) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);

  const showGalleryCopy = (target = "") => {
    const hasCustomCopy = Boolean(target);
    if (gallery) {
      gallery.classList.toggle("has-gallery-copy", hasCustomCopy);
      if (hasCustomCopy) gallery.dataset.galleryStory = target;
      else delete gallery.dataset.galleryStory;
    }
    galleryCopies.forEach((copy) => {
      copy.hidden = copy.dataset.productGalleryCopy !== target;
    });
  };

  const applyPackOption = (option, { deactivateThumbs = true } = {}) => {
    const count = Number(option.dataset.count || 1);
    const packPrice = Number(option.dataset.packPriceCents || unitPrice * count);
    packOptions.forEach((item) => {
      const selected = item === option;
      item.classList.toggle("is-selected", selected);
      item.setAttribute("aria-pressed", String(selected));
    });
    // On init the server-rendered active thumb must keep its highlight; only an
    // explicit pack click deactivates the gallery thumbs (the pack image is not
    // one of the thumb scenes).
    if (deactivateThumbs) {
      galleryThumbs.forEach((item) => {
        item.classList.remove("is-active");
        item.setAttribute("aria-pressed", "false");
      });
    }

    const image = option.dataset.image;
    const summaryImage = option.dataset.summaryImage || image;
    if (gallery) {
      gallery.dataset.packCount = String(count);
    }
    showGalleryCopy();
    if (mainImage && image) mainImage.src = image;
    if (summaryImage) summaryImages.forEach((item) => { item.src = summaryImage; });
    unitLabels.forEach((item) => { item.textContent = option.dataset.unit || ""; });
    arrivalLabels.forEach((label) => { label.textContent = option.dataset.arrival || ""; });
    // Variant-backed options switch the form's variant id and always add one
    // of that variant; legacy fallback options keep multiplying quantity.
    if (variantIdInput && option.dataset.variantId) {
      variantIdInput.value = option.dataset.variantId;
      installmentVariantInputs.forEach((input) => {
        input.value = option.dataset.variantId;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
      if (quantityInput) quantityInput.value = "1";
    } else if (quantityInput) {
      quantityInput.value = String(count);
    }
    priceLabels.forEach((label) => {
      label.textContent = formatMoney(packPrice);
    });
    const compareAt = Number(option.dataset.packCompareAtCents || 0);
    compareAtLabels.forEach((label) => {
      if (compareAt > packPrice) {
        label.textContent = formatMoney(compareAt);
        label.hidden = false;
      } else {
        label.hidden = true;
      }
    });
    const savings = Number(option.dataset.packSavingsCents || Math.max(0, compareAt - packPrice));
    savingsLabels.forEach((label) => {
      if (savings > 0) {
        label.textContent = `Save ${formatMoney(savings)}`;
        label.hidden = false;
      } else {
        label.hidden = true;
      }
    });
  };

  packOptions.forEach((option) => {
    const count = Number(option.dataset.count || 1);
    const packPrice = Number(option.dataset.packPriceCents || unitPrice * count);
    const optionPrice = option.querySelector("[data-pack-price]");
    if (optionPrice) optionPrice.textContent = formatMoney(packPrice);
    option.addEventListener("click", () => applyPackOption(option));
  });

  const initialPack = packOptions.find((option) => option.classList.contains("is-selected"));
  if (initialPack) applyPackOption(initialPack, { deactivateThumbs: false });

  galleryThumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      galleryThumbs.forEach((item) => {
        const active = item === thumb;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      if (mainImage && thumb.dataset.productGalleryThumb) {
        mainImage.src = thumb.dataset.productGalleryThumb;
      }
      showGalleryCopy(thumb.dataset.productGalleryCopyTarget || "");
    });
  });

  const directCheckoutButtons = Array.from(page.querySelectorAll("[data-direct-checkout-button]"));
  directCheckoutButtons.forEach((button) => {
    button.addEventListener("click", async () => {
      const formId = button.dataset.directCheckoutForm;
      const form = formId ? document.getElementById(formId) : null;
      if (!form || form.dataset.checkoutPending === "true" || !window.fetch) return;
      form.dataset.checkoutPending = "true";

      const status = page.querySelector("[data-checkout-status]");
      const matchingButtons = directCheckoutButtons.filter((item) => item.dataset.directCheckoutForm === formId);
      const originalMarkup = matchingButtons.map((item) => item.innerHTML);
      matchingButtons.forEach((item) => {
        item.disabled = true;
        item.textContent = "Preparing checkout…";
      });
      if (status) status.textContent = "Preparing your secure checkout…";

      try {
        const response = await fetch(form.action, {
          method: "POST",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error("Unable to add this item");
        window.location.assign(button.dataset.checkoutUrl || "/checkout");
      } catch (error) {
        form.dataset.checkoutPending = "false";
        matchingButtons.forEach((item, index) => {
          item.disabled = false;
          item.innerHTML = originalMarkup[index];
        });
        if (status) status.textContent = "We couldn’t start checkout. Please try again.";
      }
    });
  });

  const reviewFilters = Array.from(page.querySelectorAll("[data-review-list-filter]"));
  const reviewCards = Array.from(page.querySelectorAll("[data-review-list-card]"));
  reviewFilters.forEach((filter) => {
    filter.addEventListener("click", () => {
      const topic = filter.dataset.reviewListFilter || "all";
      reviewFilters.forEach((item) => {
        const active = item === filter;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      reviewCards.forEach((card) => {
        const topics = (card.dataset.reviewTopics || "").split(" ");
        card.hidden = topic !== "all" && !topics.includes(topic);
      });
    });
  });

  initializeScrollStory(page.querySelector("[data-ln-ingredient-story]"));

  const sticky = page.querySelector("[data-product-sticky]");
  const buyBox = page.querySelector("[data-product-buy-box], .balls-pdp-v3__purchase");
  if (sticky && "IntersectionObserver" in window) {
    const stickyMedia = window.matchMedia("(max-width: 760px)");
    let stickyObserver;
    const observeStickyTrigger = () => {
      const trigger = stickyMedia.matches ? gallery : buyBox;
      if (!trigger) return;
      if (stickyObserver) stickyObserver.disconnect();
      stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const purchaseHasPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle("is-visible", purchaseHasPassed);
        },
        { threshold: 0.08 },
      );
      stickyObserver.observe(trigger);
    };
    observeStickyTrigger();
    stickyMedia.addEventListener?.("change", observeStickyTrigger);
  }

  page.dataset.productBound = "true";
};

const initializeStoryPage = (root = document) => {
  const page = root.matches?.("[data-ln-story-page]") ? root : root.querySelector?.("[data-ln-story-page]");
  if (!page || page.dataset.storyBound === "true") return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const revealSections = Array.from(page.querySelectorAll("[data-story-reveal]"));
  const process = page.querySelector(".story-process");
  const processSteps = Array.from(page.querySelectorAll(".story-process-step"));
  const ingredientCards = Array.from(page.querySelectorAll(".story-ingredient-card"));
  const hero = page.querySelector("[data-story-hero]");

  ingredientCards.forEach((card, index) => {
    card.style.setProperty("--ingredient-delay", `${Math.min(index, 5) * 0.08}s`);
  });

  if (!reducedMotion) page.classList.add("story-motion-ready");
  page.classList.add("story-is-visible");

  const revealObserver = new IntersectionObserver(
    (entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      if (entry.target.classList.contains("story-ingredients")) {
        ingredientCards.forEach((card) => card.classList.add("is-visible"));
      }
      revealObserver.unobserve(entry.target);
    }),
    { threshold: 0.15 },
  );
  revealSections.forEach((section) => revealObserver.observe(section));

  let requested = false;
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const update = () => {
    requested = false;
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    page.style.setProperty("--story-page-progress", String(clamp(window.scrollY / maxScroll)));

    if (hero) {
      const rect = hero.getBoundingClientRect();
      page.style.setProperty("--story-hero-progress", String(clamp(-rect.top / Math.max(1, rect.height))));
    }

    if (process) {
      const rect = process.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, rect.height - window.innerHeight));
      page.style.setProperty("--story-process-progress", String(progress));
      const activeIndex = Math.min(processSteps.length - 1, Math.floor(progress * processSteps.length));
      processSteps.forEach((step, index) => {
        step.classList.toggle("is-active", index === activeIndex);
        step.classList.toggle("is-past", index < activeIndex);
      });
    }
  };
  const requestUpdate = () => {
    if (requested) return;
    requested = true;
    window.requestAnimationFrame(update);
  };
  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate);
  requestUpdate();
  page.dataset.storyBound = "true";
};

const initializeTheme = (root = document) => {
  initializeNavigation();
  initializeShopAllLinks(root);
  initializeHomeMotion(root.querySelector?.("[data-home-motion]") || document.querySelector("[data-home-motion]"));
  initializeMoments(root);
  initializeShopCollection(root);
  initializeCart(root);
  initializeProductPage(root);
  initializeStoryPage(root);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeTheme(document), { once: true });
} else {
  initializeTheme(document);
}
document.addEventListener("shopify:section:load", (event) => initializeTheme(event.target));
